<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import UploadCard from '../Buttons/UploadCard.vue';
    import { UploadedFile, useFileUploader } from '../Form/Composable/file-upload';
    import InputDecorator, { InputDecoratorProps } from './InputDecorator.vue';
    import { useI18n } from 'vue-i18n';
    import useFormValidation from '../../Composables/form-validation';

    const props = withDefaults(defineProps<{
        id: string;
        fieldId: number;
        accept?: string[];
        disabled?: boolean;
        maxFiles?: number;
        value: string[] | string;
        outputFormat?: 'array' | 'string';
    } & InputDecoratorProps>(), {
        maxFiles: 1,
        disabled: false,
        outputFormat: 'array',
        accept: () => []
    });

    const emit = defineEmits<{
        update: [string[] | string];
    }>();

    const { t } = useI18n();
    const { upload } = useFileUploader(props.fieldId);
    const uploadedFiles = ref<Record<string, UploadedFile>>({});

    const { errors: fieldErrors, addErrors, clearErrors } = useFormValidation();

    onMounted(() => {
        const initialValue = Array.isArray(props.value) ? props.value : (props.value) ? [props.value] : [];
        uploadedFiles.value = initialValue.reduce((acc, url) => {
            const name = url.split('/').pop() || '';
            return {
                ...acc,
                [name]: {
                    file: new File([], name),
                    url
                }
            };
        }, {});
    });

    const onFilesSelected = async (files: File[]) => {
        clearErrors();

        // Remove all files that are not in the new selection
        Object.keys(uploadedFiles.value).forEach((key) => {
            if (!files.find((f) => f.name === key)) {
                delete uploadedFiles.value[key];
            }
        });

        await Promise.all(files.map(async (f) => {
            const response = await upload(f, (progress) => {
                uploadedFiles.value = {
                    ...uploadedFiles.value,
                    [f.name]: {
                        file: f,
                        url: '',
                        progress
                    }
                };
            });
            if (response.data.success) {
                uploadedFiles.value = {
                    ...uploadedFiles.value,
                    [f.name]: {
                        file: f,
                        url: response.data.data.link,
                        progress: uploadedFiles.value[f.name].progress
                    }
                };
            } else {
                delete uploadedFiles.value[f.name];
                addErrors('uploadErrors', response.data.messages || []);
            }
        }));
        const updatedFiles = Object.values(uploadedFiles.value).map((f) => f.url);
        emit('update', props.outputFormat === 'array' ? updatedFiles : updatedFiles[0] || '');
    };

    const allErrors = computed(() => {
        return [
            ...fieldErrors.value.uploadErrors || [],
            ...props.errors || []
        ];
    });
</script>
<template>
    <InputDecorator
        :help="help"
        :label="label"
        :required="required"
        :help-position="helpPosition"
        :for-id="id"
        :errors="allErrors"
    >
        <UploadCard
            :name="id"
            :files="Object.values(uploadedFiles)"
            :accept="accept"
            :disabled="disabled"
            :max-files="maxFiles"
            @files-selected="onFilesSelected"
            :title="t('field.file.upload')"
            :replace-title="t('field.file.replace')"
        >
            <i class="upload-icon far fa-circle-arrow-up"/>
        </UploadCard>
    </InputDecorator>
</template>
<style lang="scss" scoped>
@import '@Common/Styles/variables';

.upload-icon {
    font-size: 25px;
    color: var(--gray-500, map-get($yapla-gray, 500));
}

</style>